import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X, MapPin, Phone, Mail, Clock, Image as ImageIcon } from "lucide-react";
import AdminNavbar from "../../../AdminNavbar";
import { clinicService } from "../../../../services";

const AdminClinicCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    website: "",
    description: "",
    mainCategory: "",
    specialties: [],
    facilities: [],
    amenities: [],
    workingHours: {
      monday: { open: "09:00", close: "18:00", isClosed: false },
      tuesday: { open: "09:00", close: "18:00", isClosed: false },
      wednesday: { open: "09:00", close: "18:00", isClosed: false },
      thursday: { open: "09:00", close: "18:00", isClosed: false },
      friday: { open: "09:00", close: "18:00", isClosed: false },
      saturday: { open: "09:00", close: "14:00", isClosed: false },
      sunday: { open: "09:00", close: "17:00", isClosed: true },
    },
    status: "active",
    emergencyContact: "",
    isEmergencyCenter: false,
    is24Hours: false,
    hasParking: true,
    hasWheelchairAccess: true,
  });

  const [newSpecialty, setNewSpecialty] = useState("");
  const [newFacility, setNewFacility] = useState("");
  const [newAmenity, setNewAmenity] = useState("");
  const [clinicImage, setClinicImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: field === 'isClosed' ? value : value
        }
      }
    }));
  };

  const handleArrayField = (field, action, value = null, index = null) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;

      switch (action) {
        case 'add':
          newArray = [...currentArray, value];
          break;
        case 'remove':
          newArray = currentArray.filter((_, i) => i !== index);
          break;
        default:
          newArray = currentArray;
      }

      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setClinicImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setClinicImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.type || !formData.email || !formData.phone || 
        !formData.address || !formData.city || !formData.state || !formData.pincode) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      // Prepare data for API (matching the API structure)
      const submitData = {
        name: formData.name,
        type: formData.type,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        ...(formData.website && { website: formData.website }),
        ...(formData.description && { description: formData.description }),
        ...(formData.status && { status: formData.status }),
        ...(formData.mainCategory && { mainCategory: formData.mainCategory }),
        ...(formData.specialties.length > 0 && { specialties: formData.specialties }),
        ...(formData.facilities.length > 0 && { facilities: formData.facilities }),
        ...(formData.amenities.length > 0 && { amenities: formData.amenities }),
        ...(formData.workingHours && { workingHours: formData.workingHours }),
        ...(formData.emergencyContact && { emergencyContact: formData.emergencyContact }),
        isEmergencyCenter: formData.isEmergencyCenter,
        is24Hours: formData.is24Hours,
        hasParking: formData.hasParking,
        hasWheelchairAccess: formData.hasWheelchairAccess,
      };
      
      // Create clinic via API (with image if provided)
      await clinicService.createClinic(submitData, clinicImage || null);
      
      toast.success("Clinic created successfully!");
      navigate("/admin/clinics");
    } catch (error) {
      console.error("Error creating clinic:", error);
      toast.error(error.message || "Failed to create clinic. Please try again.");
    }
  };


  const days = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/clinics">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clinics
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Clinic</h1>
              <p className="text-gray-600 mt-2">Add a new healthcare facility to the platform</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Essential details about the clinic
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Clinic Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="City Medical Center"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="type">Clinic Type *</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(value) => handleInputChange("type", value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select clinic type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Primary Care">Primary Care</SelectItem>
                          <SelectItem value="Specialty Clinic">Specialty Clinic</SelectItem>
                          <SelectItem value="Hospital">Hospital</SelectItem>
                          <SelectItem value="Emergency Center">Emergency Center</SelectItem>
                          <SelectItem value="Diagnostic Center">Diagnostic Center</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="contact@citymedical.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        placeholder="+91-1234567890"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        placeholder="Mumbai"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => handleInputChange("state", e.target.value)}
                        placeholder="Maharashtra"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pincode">Pincode *</Label>
                      <Input
                        id="pincode"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange("pincode", e.target.value)}
                        placeholder="400001"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleInputChange("website", e.target.value)}
                        placeholder="https://www.citymedical.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyContact">Emergency Contact</Label>
                      <Input
                        id="emergencyContact"
                        value={formData.emergencyContact}
                        onChange={(e) => handleInputChange("emergencyContact", e.target.value)}
                        placeholder="+91-9876543210"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleInputChange("status", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mainCategory">Main Category</Label>
                      <Select
                        value={formData.mainCategory}
                        onValueChange={(value) => handleInputChange("mainCategory", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select main category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cardiology">Cardiology</SelectItem>
                          <SelectItem value="dermatology">Dermatology</SelectItem>
                          <SelectItem value="neurology">Neurology</SelectItem>
                          <SelectItem value="orthopedics">Orthopedics</SelectItem>
                          <SelectItem value="pediatrics">Pediatrics</SelectItem>
                          <SelectItem value="psychiatry">Psychiatry</SelectItem>
                          <SelectItem value="surgery">Surgery</SelectItem>
                          <SelectItem value="ophthalmology">Ophthalmology</SelectItem>
                          <SelectItem value="dentistry">Dentistry</SelectItem>
                          <SelectItem value="gynecology">Gynecology</SelectItem>
                          <SelectItem value="urology">Urology</SelectItem>
                          <SelectItem value="pulmonology">Pulmonology</SelectItem>
                          <SelectItem value="laparoscopy">Laparoscopy</SelectItem>
                          <SelectItem value="medicine">Medicine</SelectItem>
                          <SelectItem value="nephrology">Nephrology</SelectItem>
                          <SelectItem value="gastro">Gastro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Complete Address *</Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      placeholder="123 Main Street, Downtown Area"
                      rows={3}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder="Brief description about the clinic, services offered, and approach to patient care"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Specialties & Facilities</CardTitle>
                  <CardDescription>
                    Medical specialties and available facilities
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Specialties</Label>
                    <div className="space-y-2">
                      {formData.specialties.map((spec, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input value={spec} readOnly />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleArrayField("specialties", "remove", null, index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="space-y-2">
                        <Select
                          value=""
                          onValueChange={(value) => {
                            if (value && !formData.specialties.includes(value)) {
                              handleArrayField("specialties", "add", value);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a specialty to add" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cardiology">Cardiology</SelectItem>
                            <SelectItem value="Dermatology">Dermatology</SelectItem>
                            <SelectItem value="Neurology">Neurology</SelectItem>
                            <SelectItem value="Orthopedics">Orthopedics</SelectItem>
                            <SelectItem value="Pediatrics">Pediatrics</SelectItem>
                            <SelectItem value="Psychiatry">Psychiatry</SelectItem>
                            <SelectItem value="Surgery">Surgery</SelectItem>
                            <SelectItem value="Ophthalmology">Ophthalmology</SelectItem>
                            <SelectItem value="Dentistry">Dentistry</SelectItem>
                            <SelectItem value="Gynecology">Gynecology</SelectItem>
                            <SelectItem value="Urology">Urology</SelectItem>
                            <SelectItem value="Pulmonology">Pulmonology</SelectItem>
                            <SelectItem value="Laparoscopy">Laparoscopy</SelectItem>
                            <SelectItem value="Medicine">Medicine</SelectItem>
                            <SelectItem value="Nephrology">Nephrology</SelectItem>
                            <SelectItem value="Gastro">Gastro</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <Input
                            value={newSpecialty}
                            onChange={(e) => setNewSpecialty(e.target.value)}
                            placeholder="Or type a custom specialty"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
                                  handleArrayField("specialties", "add", newSpecialty.trim());
                                  setNewSpecialty("");
                                }
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
                                handleArrayField("specialties", "add", newSpecialty.trim());
                                setNewSpecialty("");
                              }
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Facilities</Label>
                    <div className="space-y-2">
                      {formData.facilities.map((facility, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input value={facility} readOnly />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleArrayField("facilities", "remove", null, index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input
                          value={newFacility}
                          onChange={(e) => setNewFacility(e.target.value)}
                          placeholder="Add facility (e.g., X-Ray, MRI)"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newFacility.trim()) {
                                handleArrayField("facilities", "add", newFacility.trim());
                                setNewFacility("");
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newFacility.trim()) {
                              handleArrayField("facilities", "add", newFacility.trim());
                              setNewFacility("");
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Amenities</Label>
                    <div className="space-y-2">
                      {formData.amenities.map((amenity, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input value={amenity} readOnly />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleArrayField("amenities", "remove", null, index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input
                          value={newAmenity}
                          onChange={(e) => setNewAmenity(e.target.value)}
                          placeholder="Add amenity (e.g., WiFi, Parking)"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newAmenity.trim()) {
                                handleArrayField("amenities", "add", newAmenity.trim());
                                setNewAmenity("");
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newAmenity.trim()) {
                              handleArrayField("amenities", "add", newAmenity.trim());
                              setNewAmenity("");
                            }
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Working Hours</CardTitle>
                  <CardDescription>
                    Set the clinic's operating hours for each day
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {days.map((day) => (
                      <div key={day.key} className="flex items-center gap-4 p-4 border rounded-lg">
                        <div className="w-24">
                          <Label className="font-medium">{day.label}</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`closed-${day.key}`}
                            checked={formData.workingHours[day.key].isClosed}
                            onChange={(e) => handleWorkingHoursChange(day.key, "isClosed", e.target.checked)}
                            className="rounded"
                          />
                          <Label htmlFor={`closed-${day.key}`} className="text-sm">Closed</Label>
                        </div>
                        {!formData.workingHours[day.key].isClosed && (
                          <>
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Open:</Label>
                              <Input
                                type="time"
                                value={formData.workingHours[day.key].open}
                                onChange={(e) => handleWorkingHoursChange(day.key, "open", e.target.value)}
                                className="w-32"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Close:</Label>
                              <Input
                                type="time"
                                value={formData.workingHours[day.key].close}
                                onChange={(e) => handleWorkingHoursChange(day.key, "close", e.target.value)}
                                className="w-32"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Clinic Image</CardTitle>
                  <CardDescription>
                    Upload an image for the clinic
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {imagePreview ? (
                    <div className="space-y-2">
                      <div className="relative w-full aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={imagePreview}
                          alt="Clinic preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={removeImage}
                        className="w-full"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Image
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <ImageIcon className="w-10 h-10 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Additional Settings</CardTitle>
                  <CardDescription>
                    Clinic features and options
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="isEmergencyCenter" className="cursor-pointer">
                      Emergency Center
                    </Label>
                    <input
                      type="checkbox"
                      id="isEmergencyCenter"
                      checked={formData.isEmergencyCenter}
                      onChange={(e) => handleInputChange("isEmergencyCenter", e.target.checked)}
                      className="rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="is24Hours" className="cursor-pointer">
                      24 Hours Service
                    </Label>
                    <input
                      type="checkbox"
                      id="is24Hours"
                      checked={formData.is24Hours}
                      onChange={(e) => handleInputChange("is24Hours", e.target.checked)}
                      className="rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="hasParking" className="cursor-pointer">
                      Has Parking
                    </Label>
                    <input
                      type="checkbox"
                      id="hasParking"
                      checked={formData.hasParking}
                      onChange={(e) => handleInputChange("hasParking", e.target.checked)}
                      className="rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="hasWheelchairAccess" className="cursor-pointer">
                      Wheelchair Accessible
                    </Label>
                    <input
                      type="checkbox"
                      id="hasWheelchairAccess"
                      checked={formData.hasWheelchairAccess}
                      onChange={(e) => handleInputChange("hasWheelchairAccess", e.target.checked)}
                      className="rounded"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button type="submit" className="w-full">
                    <Save className="h-4 w-4 mr-2" />
                    Create Clinic
                  </Button>
                  <Link to="/admin/clinics">
                    <Button variant="outline" className="w-full">
                      Cancel
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminClinicCreate;
